import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { apiErrorMessage } from '../../shared/api/errors';
import { formatDate, fullName } from '../../shared/lib/format';
import { Button } from '../../shared/ui/Button';
import { inputClass } from '../../shared/ui/TextField';
import { Card, ErrorText, Spinner } from '../../shared/ui/primitives';
import { useSession } from '../auth/session';
import {
  createExamination,
  downloadExaminationArchive,
  listPatientExaminations,
} from '../examinations/api';
import { STUDY_TYPES } from '../examinations/types';
import {
  assignDoctor,
  deletePatient,
  getPatient,
  listPatientDoctors,
  listPatientShares,
  sharePatient,
  unassignDoctor,
  unsharePatient,
} from './api';

/** Локальная дата в формате YYYY-MM-DD (без сдвига часового пояса). */
function todayISO(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

export function PatientDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useSession();
  const isAdmin = user?.role === 'admin';

  // Открыт из карточки врача (вкладка «Врачи») — «назад» должно вести туда, а не в общий список.
  const [searchParams] = useSearchParams();
  const backTo = searchParams.get('from') || '/patients';
  const backLabel = backTo.startsWith('/admin/doctors') ? '← К карточке врача' : '← К списку';

  const patient = useQuery({ queryKey: ['patient', id], queryFn: () => getPatient(id), enabled: Boolean(id) });
  const canManageShares = isAdmin || Boolean(patient.data?.viewerIsLead);

  const doctors = useQuery({
    queryKey: ['patient', id, 'doctors'],
    queryFn: () => listPatientDoctors(id),
    enabled: Boolean(id) && isAdmin,
  });
  const shares = useQuery({
    queryKey: ['patient', id, 'shares'],
    queryFn: () => listPatientShares(id),
    enabled: Boolean(id) && canManageShares,
  });
  const examinations = useQuery({
    queryKey: ['patient', id, 'examinations'],
    queryFn: () => listPatientExaminations(id),
    enabled: Boolean(id),
  });

  const [leadEmail, setLeadEmail] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [examDate, setExamDate] = useState('');
  const [examToday, setExamToday] = useState(false);
  const [examModality, setExamModality] = useState('');
  const invalidateDoctors = () => queryClient.invalidateQueries({ queryKey: ['patient', id, 'doctors'] });
  const invalidateShares = () => queryClient.invalidateQueries({ queryKey: ['patient', id, 'shares'] });

  const createExam = useMutation({
    mutationFn: () =>
      createExamination({ patientId: id, examinedAt: examDate, modality: examModality.trim() }),
    onSuccess: (exam) => {
      setExamDate('');
      setExamToday(false);
      setExamModality('');
      navigate(`/examinations/${exam.id}`);
    },
  });

  const downloadArchive = useMutation({
    mutationFn: (examId: string) => downloadExaminationArchive(examId),
  });

  const assign = useMutation({
    mutationFn: (email: string) => assignDoctor(id, email),
    onSuccess: async () => {
      setLeadEmail('');
      await invalidateDoctors();
    },
  });
  const unassign = useMutation({
    mutationFn: (doctorId: string) => unassignDoctor(id, doctorId),
    onSuccess: invalidateDoctors,
  });
  const share = useMutation({
    mutationFn: (email: string) => sharePatient(id, email),
    onSuccess: async () => {
      setShareEmail('');
      await invalidateShares();
    },
  });
  const unshare = useMutation({
    mutationFn: (doctorId: string) => unsharePatient(id, doctorId),
    onSuccess: invalidateShares,
  });
  const remove = useMutation({
    mutationFn: () => deletePatient(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['patients'] });
      navigate('/patients', { replace: true });
    },
  });

  if (patient.isLoading) return <Spinner />;
  if (patient.isError || !patient.data) {
    return <ErrorText>{apiErrorMessage(patient.error, 'Карточка недоступна')}</ErrorText>;
  }

  const p = patient.data;

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <Link to={backTo} className="text-sm font-medium text-accent hover:text-accent-600">
          {backLabel}
        </Link>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">{fullName(p)}</h1>
          <div className="flex gap-2">
            {isAdmin && (
              <Link
                to={`/patients/${id}/edit`}
                className="rounded-lg border border-line bg-surface px-3 py-1.5 text-sm text-ink hover:bg-canvas"
              >
                Редактировать
              </Link>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm('Удалить карточку пациента? Действие необратимо.')) remove.mutate();
                }}
                className="rounded-lg border border-danger/30 px-3 py-1.5 text-sm text-danger hover:bg-danger-050"
              >
                Удалить
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="p-4 sm:p-5">
          <dl className="space-y-2.5 text-sm">
            <Row label="Дата рождения" value={formatDate(p.birthDate)} />
            <Row label="Телефон" value={p.phone || '—'} />
            <Row label="E-mail" value={p.email || '—'} />
            <Row label="Создана" value={formatDate(p.createdAt)} />
            {p.notes && (
              <div className="border-t border-line pt-2.5">
                <dt className="text-muted">Заметки</dt>
                <dd className="mt-1 whitespace-pre-wrap text-ink">{p.notes}</dd>
              </div>
            )}
          </dl>
        </Card>

        {isAdmin && (
          <Card className="space-y-3 p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-ink">Ведущие врачи</h2>
            {doctors.isLoading && <Spinner />}
            <ul className="space-y-1.5 text-sm">
              {(doctors.data ?? []).map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2">
                  <span className="text-ink">
                    {d.email}
                    {d.id === user?.id && <span className="ml-2 text-xs text-muted">(вы)</span>}
                  </span>
                  <button
                    type="button"
                    onClick={() => unassign.mutate(d.id)}
                    className="text-xs text-muted hover:text-danger"
                  >
                    снять
                  </button>
                </li>
              ))}
              {doctors.data?.length === 0 && <li className="text-muted">Не назначены</li>}
            </ul>

            <EmailAddForm
              value={leadEmail}
              onChange={setLeadEmail}
              placeholder="E-mail врача"
              submitLabel="Назначить"
              pending={assign.isPending}
              onSubmit={(email) => assign.mutate(email)}
            />
            {assign.isError && (
              <p className="text-xs text-danger">{apiErrorMessage(assign.error, 'Не удалось назначить')}</p>
            )}
          </Card>
        )}

        {canManageShares && (
          <Card className="space-y-3 p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-ink">Доступ к карточке</h2>
            <p className="text-xs text-muted">
              {isAdmin
                ? 'Врачи, которым открыт просмотр карточки.'
                : 'Врачи, которым вы открыли просмотр этой карточки.'}
            </p>
            {shares.isLoading && <Spinner />}
            <ul className="space-y-1.5 text-sm">
              {(shares.data ?? []).map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2">
                  <span className="text-ink">
                    {d.email}
                    {isAdmin && d.sharedByEmail && (
                      <span className="ml-2 text-xs text-muted">открыл: {d.sharedByEmail}</span>
                    )}
                  </span>
                  <button
                    type="button"
                    onClick={() => unshare.mutate(d.id)}
                    className="text-xs text-muted hover:text-danger"
                  >
                    отозвать
                  </button>
                </li>
              ))}
              {shares.data?.length === 0 && <li className="text-muted">Пока никому не открыт</li>}
            </ul>

            <EmailAddForm
              value={shareEmail}
              onChange={setShareEmail}
              placeholder="E-mail коллеги"
              submitLabel="Поделиться"
              pending={share.isPending}
              onSubmit={(email) => share.mutate(email)}
            />
            {share.isError && (
              <p className="text-xs text-danger">
                {apiErrorMessage(share.error, 'Не удалось поделиться')}
              </p>
            )}
          </Card>
        )}
      </div>

      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-ink">Обследования</h2>

        {isAdmin && (
          <Card className="space-y-3 p-4 sm:p-5">
            <p className="text-sm font-medium text-ink">Новое обследование</p>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
              <div className="block text-sm sm:flex-1">
                <span className="mb-1 block text-xs text-muted">Дата обследования</span>
                <input
                  type="date"
                  value={examDate}
                  disabled={examToday}
                  onChange={(e) => setExamDate(e.target.value)}
                  className={`${inputClass} disabled:opacity-60`}
                />
                <label className="mt-1.5 flex items-center gap-1.5 text-xs text-muted">
                  <input
                    type="checkbox"
                    checked={examToday}
                    onChange={(e) => {
                      setExamToday(e.target.checked);
                      if (e.target.checked) setExamDate(todayISO());
                    }}
                  />
                  Сегодня
                </label>
              </div>
              <label className="block text-sm sm:flex-1">
                <span className="mb-1 block text-xs text-muted">Вид исследования</span>
                <select
                  value={examModality}
                  onChange={(e) => setExamModality(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Не указан</option>
                  {STUDY_TYPES.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <div className="sm:flex sm:flex-col">
                <span aria-hidden className="mb-1 hidden text-xs sm:block">
                  &nbsp;
                </span>
                <Button
                  type="button"
                  size="sm"
                  className="w-full sm:h-[38px] sm:w-auto"
                  disabled={!examDate || createExam.isPending}
                  onClick={() => createExam.mutate()}
                >
                  Создать
                </Button>
              </div>
            </div>
            {createExam.isError && (
              <p className="text-xs text-danger">
                {apiErrorMessage(createExam.error, 'Не удалось создать обследование')}
              </p>
            )}
          </Card>
        )}

        {examinations.isLoading && <Spinner />}
        {examinations.data && examinations.data.items.length > 0 ? (
          <Card className="divide-y divide-line overflow-hidden">
            {examinations.data.items.map((e) => (
              <div
                key={e.id}
                className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <Link to={`/examinations/${e.id}`} className="min-w-0 hover:text-accent">
                  <div>
                    <span className="font-medium text-ink">
                      Дата обследования: {formatDate(e.examinedAt)}
                    </span>
                    <span className="ml-2 text-xs text-muted">
                      {e.modality || '—'} · файлов: {e.fileCount}
                    </span>
                  </div>
                  <span className="text-xs text-muted">
                    Дата загрузки: {formatDate(e.createdAt)}
                  </span>
                </Link>
                <button
                  type="button"
                  disabled={e.fileCount === 0 || downloadArchive.isPending}
                  onClick={() => downloadArchive.mutate(e.id)}
                  className="text-xs font-medium text-accent hover:text-accent-600 disabled:opacity-40"
                >
                  Скачать архив
                </button>
              </div>
            ))}
          </Card>
        ) : (
          !examinations.isLoading && <p className="text-sm text-muted">Обследований пока нет.</p>
        )}
        {downloadArchive.isError && (
          <p className="text-xs text-danger">
            {apiErrorMessage(downloadArchive.error, 'Не удалось скачать архив')}
          </p>
        )}
      </div>
    </section>
  );
}

/** Поле e-mail врача + кнопка: врачей много, поэтому вместо выпадающего
 *  списка вводится адрес, под которым коллега зарегистрирован в системе. */
function EmailAddForm({
  value,
  onChange,
  placeholder,
  submitLabel,
  pending,
  onSubmit,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  submitLabel: string;
  pending: boolean;
  onSubmit: (email: string) => void;
}) {
  const email = value.trim();
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  return (
    <form
      className="flex flex-col gap-2 border-t border-line pt-3 sm:flex-row"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !pending) onSubmit(email);
      }}
    >
      <input
        type="email"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off"
        className={`${inputClass} sm:flex-1`}
      />
      <Button type="submit" size="sm" disabled={!valid || pending}>
        {submitLabel}
      </Button>
    </form>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right text-ink">{value}</dd>
    </div>
  );
}
